import React, { useContext, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import {
  filtersToQueryObject,
  getStringQuery,
} from '@prisjakt/product-collection-urls';

import { AppContext } from 'src/components/app-context';
import WordCloud from 'src/components/word-cloud';
import { PageContent } from 'src/components/page';
import { useViewportContext } from 'src/components/viewport/context';
import { AdvizedCarousel } from 'src/components/advized';
import FAQ from 'src/areas/editorial-content/components/slices/faq';

import {
  SecondaryNavigation,
  Description,
  Products,
  DealsContextWrapper,
  Filter,
  Breadcrumb,
  HeaderBlock,
  Navigation,
  FeaturedArticles,
} from './components';

import type { ProductCollection as ProductCollectionType } from '../../types';

import RouteContext from '../../routes/context';
import { FilterContext } from './filter-context';
import { FilterScope } from 'src/globals/tracking/filter';
import {
  ProductCollectionFilterContext,
  ProductCollectionFilterTrackingScopeContext,
  ProductCollectionTrackingContext,
} from './filter-tracking';

import { formatBreadcrumbs } from './utils/format-breadcrumbs';
import { discriminatedArrayToMap } from 'src/utils';
import { SiteWidthWrapper } from './styles';

type Props = {
  data: {
    productCollection: ProductCollectionType;
  };
};

const getProductCollectionPathKey = (state: any) => {
  const filters = (state?.filters || []).map((filter: any) =>
    filter.type === 'range'
      ? {
          // Don't update pathKey for different ranges
          ...filter,
          from: '',
          to: '',
        }
      : filter
  );
  return `${getStringQuery(filtersToQueryObject(filters))}:${
    state?.query?.offset || 0
  }`;
};

const trackingScopeContextValue = {
  scope: FilterScope.CATEGORY,
};

const productCollectionFilterContextValue = {
  filterLayout: 'facet-navigation' as const,
};

const ProductCollection = ({ data }: Props) => {
  const { t, linkTo } = useContext(AppContext);
  const { getState } = useContext(RouteContext);
  const dispatch = useDispatch();
  const { isMobile, isTablet } = useViewportContext();
  const removeHeaderMargins = isMobile || isTablet;

  const currentState = getState();
  const { productCollection } = data;
  const { popularSearchTerms, slices, slug, name } = productCollection || {};
  const pathKey = getProductCollectionPathKey(currentState);

  const isPaginated = currentState.offset > 0;

  useEffect(() => {
    ReactDOM.flushSync(() => {
      dispatch({
        type: 'ADS_PATHKEY_UPDATE',
        pathKey,
      });
    });
  }, [dispatch, pathKey]);

  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [count, setCount] = useState(0);

  const {
    BreadcrumbsSlice,
    HeaderBlockSlice,
    FilterSlice,
    NavigationSlice,
    ProductsSlice,
    DescriptionSlice,
    PrismicSliceFaqSection,
    FeaturedArticlesSlice,
    ExpertSlice,
    SecondaryNavigationSlice,
  } = discriminatedArrayToMap(slices, '__typename');

  const trackingContextValue = useMemo(
    () => ({ categoryId: slug, categoryName: name }),
    [slug, name]
  );

  const filterContextValue = useMemo(
    () => ({ mobileFilterOpen, setMobileFilterOpen, count, setCount }),
    [count, mobileFilterOpen]
  );

  return (
    <ProductCollectionFilterTrackingScopeContext.Provider
      value={trackingScopeContextValue}
    >
      <ProductCollectionTrackingContext.Provider value={trackingContextValue}>
        <FilterContext.Provider value={filterContextValue}>
          <PageContent data-test="ProductCollectionPage" withAds>
            {BreadcrumbsSlice ? (
              <Breadcrumb
                id={BreadcrumbsSlice.id}
                items={formatBreadcrumbs(
                  BreadcrumbsSlice.items,
                  productCollection.categoryPath
                )}
                categoryIds={productCollection.categories?.map(
                  category => category.id
                )}
              />
            ) : null}
            {HeaderBlockSlice ? (
              <HeaderBlock
                title={HeaderBlockSlice.title}
                pagination={HeaderBlockSlice.pagination}
                noMargins={removeHeaderMargins}
              />
            ) : null}
            {FilterSlice ? (
              <Filter
                id={FilterSlice.id}
                sections={FilterSlice.sections}
                filters={FilterSlice.filters}
                productsCount={FilterSlice.productsCount}
              />
            ) : null}
            {NavigationSlice ? (
              <ProductCollectionFilterContext.Provider
                value={productCollectionFilterContextValue}
              >
                <Navigation
                  id={NavigationSlice.id}
                  links={NavigationSlice.links}
                  property={NavigationSlice.property}
                  key={`${NavigationSlice.id}_${
                    Array.isArray(NavigationSlice.links)
                      ? NavigationSlice.links.map(({ id }) => id).join(',')
                      : ''
                  }`}
                />
              </ProductCollectionFilterContext.Provider>
            ) : null}
            {ProductsSlice ? (
              <DealsContextWrapper dealsItems={ProductsSlice.dealsItems}>
                <Products
                  id={ProductsSlice.id}
                  pageInfo={ProductsSlice.pageInfo}
                  products={ProductsSlice.products}
                  productsCount={ProductsSlice.productsCount}
                  storeCount={ProductsSlice.storeCount}
                  legalText={ProductsSlice.legalText}
                  availableSorts={ProductsSlice.availableSorts}
                  currentSort={ProductsSlice.currentSort}
                  maxHitsAmount={ProductsSlice.maxHitsAmount}
                  sortDirection={ProductsSlice.sortDirection}
                  propertyColumns={ProductsSlice.propertyColumns}
                  expert={ExpertSlice?.expert}
                  productCollection={productCollection}
                  preferredViewMode={ProductsSlice.preferredViewMode}
                />
              </DealsContextWrapper>
            ) : null}
            <SiteWidthWrapper>
              {DescriptionSlice ? (
                <Description
                  productCollection={productCollection}
                  description={DescriptionSlice.description}
                />
              ) : null}
              {PrismicSliceFaqSection ? (
                <FAQ
                  faqTitle={PrismicSliceFaqSection.faqTitle}
                  items={PrismicSliceFaqSection.items}
                />
              ) : null}
              {FeaturedArticlesSlice ? (
                <FeaturedArticles
                  featuredArticlesSlice={FeaturedArticlesSlice}
                />
              ) : null}
              {!isPaginated && ExpertSlice ? (
                <AdvizedCarousel
                  offset={ExpertSlice.offset}
                  isList={false}
                  numberOfCards={isTablet ? 5 : 3}
                  hideExperts={false}
                  {...ExpertSlice.expert}
                />
              ) : null}
              {SecondaryNavigationSlice ? (
                <SecondaryNavigation
                  id={SecondaryNavigationSlice.id}
                  key={SecondaryNavigationSlice.id}
                  productCollection={productCollection}
                  navigationItems={SecondaryNavigationSlice.navigationItems}
                />
              ) : null}
              {Array.isArray(popularSearchTerms) &&
              popularSearchTerms.length ? (
                <WordCloud
                  title={t('search', 'search.related-searches')}
                  items={popularSearchTerms.slice(0, 100)}
                  showMoreUrl={linkTo('keywordListing', productCollection)}
                  numberOfVisibleItems={35}
                />
              ) : null}
            </SiteWidthWrapper>
          </PageContent>
        </FilterContext.Provider>
      </ProductCollectionTrackingContext.Provider>
    </ProductCollectionFilterTrackingScopeContext.Provider>
  );
};

export default ProductCollection;
